import type { ContentFieldExtension } from 'dc-extensions-sdk'
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactElement } from 'react'
import { useRouteLoaderData } from 'react-router'
import type { Category } from '../api/categoriesApi.ts'
import App, { type ConfirmationModalState } from '../App.tsx'
import { DEFAULT_CATALOG_ID } from '../components/CatalogSelector.tsx'
import { setRuntimeCatalogId } from '../config/api.ts'
import { hasCredentials, setCredentials, setRuntimeEnv } from '../config/credentials.ts'
import type { SelectedCategory } from '../types/category.ts'

type FieldModel = SelectedCategory | undefined

/**
 * Root loader data type
 */
interface RootLoaderData {
    env: {
        OCAPI_CLIENT_ID: string
        OCAPI_CLIENT_SECRET: string
        SITE_ID: string
        CATALOG_ID: string
    }
}

// Re-export for backwards compatibility
export type { SelectedCategory }

/**
 * Amplience installation params structure for SFCC integration.
 */
interface InstallationParams {
    readonly vendor?: string
    readonly codec_params?: {
        readonly site_id?: string
        readonly client_id?: string
        readonly client_secret?: string
    }
}

/**
 * Home route component - the main category search extension
 *
 * This route handles:
 * - Amplience SDK initialization (client-side only)
 * - SFCC credentials configuration
 * - Category selection state management
 * - Saving selections to Amplience
 */
export default function Home(): ReactElement {
    // Get runtime environment variables from root loader
    const rootData = useRouteLoaderData<RootLoaderData>('root')

    const [selectedCategory, setSelectedCategory] = useState<SelectedCategory | undefined>()
    const [selectedCatalogId, setSelectedCatalogId] = useState<string>(DEFAULT_CATALOG_ID)
    const [error, setError] = useState<string | undefined>()
    const [categorySelectionError, setCategorySelectionError] = useState<string | undefined>()
    const [isInitialized, setIsInitialized] = useState(false)
    const [confirmationModal, setConfirmationModal] = useState<ConfirmationModalState | undefined>()
    const sdkRef = useRef<ContentFieldExtension<FieldModel> | null>(null)
    const isInitialLoadRef = useRef(true)
    const selectedCategoryRef = useRef<SelectedCategory | undefined>(undefined)

    useLayoutEffect(() => {
        selectedCategoryRef.current = selectedCategory
    }, [selectedCategory])

    // Set runtime environment variables from server loader
    useEffect(() => {
        if (rootData?.env) {
            setRuntimeEnv({
                clientId: rootData.env.OCAPI_CLIENT_ID,
                clientSecret: rootData.env.OCAPI_CLIENT_SECRET,
                siteId: rootData.env.SITE_ID
            })
            if (rootData.env.CATALOG_ID) {
                setRuntimeCatalogId(rootData.env.CATALOG_ID)
            }
        }
    }, [rootData])

    useEffect(() => {
        // Only run on client side
        if (typeof window === 'undefined') {
            return
        }

        const SDK_TIMEOUT_MS = 2000 // 2 seconds timeout for SDK init

        /**
         * Initialize the Amplience SDK and configure SFCC credentials.
         * Handles both embedded (inside Amplience iframe) and standalone modes.
         */
        const initializeSdkAndCredentials = async (): Promise<void> => {
            try {
                // Dynamically import the Amplience SDK to avoid SSR issues
                const { init } = await import('dc-extensions-sdk')

                // Race between SDK init and timeout
                // SDK init hangs forever when not inside Amplience iframe
                const sdk = await Promise.race([
                    init<ContentFieldExtension<FieldModel>>(),
                    new Promise<never>((_, reject) =>
                        setTimeout(() => reject(new Error('SDK timeout')), SDK_TIMEOUT_MS)
                    )
                ])

                sdkRef.current = sdk
                sdk.frame.startAutoResizer()

                // Extract SFCC credentials from Amplience installation params
                const installation = sdk.params?.installation as InstallationParams | undefined
                const codecParams = installation?.codec_params

                if (codecParams?.client_id && codecParams?.client_secret) {
                    setCredentials({
                        clientId: codecParams.client_id,
                        clientSecret: codecParams.client_secret,
                        siteId: codecParams.site_id
                    })
                } else {
                    console.warn(
                        'SFCC credentials not found in Amplience installation params. ' +
                            'Falling back to environment variables.'
                    )
                }

                const fieldValue = await sdk.field.getValue()

                if (fieldValue) {
                    setSelectedCategory(fieldValue)
                }

                setIsInitialized(true)
            } catch {
                // SDK init failed or timed out - expected when running locally
                console.log('Running in standalone mode (outside Amplience)')

                // Check if we have credentials from runtime env or .env.local
                if (!hasCredentials()) {
                    setError(
                        'SFCC credentials not configured. Set instance environment variables ' +
                            '(OCAPI_CLIENT_ID/OCAPI_CLIENT_SECRET) or create .env.local with ' +
                            'VITE_OCAPI_CLIENT_ID and VITE_OCAPI_CLIENT_SECRET for local development.'
                    )
                }
                // If we have .env.local credentials, proceed without error
                setIsInitialized(true)
            }
        }

        void initializeSdkAndCredentials()
    }, [])

    const saveToAmplience = useCallback(
        async (category: SelectedCategory | undefined): Promise<void> => {
            if (sdkRef.current) {
                try {
                    await sdkRef.current.field.setValue(category)
                } catch (saveError) {
                    console.error('Failed to save category to Amplience:', saveError)
                    setError('Failed to save category selection')
                }
            }
        },
        []
    )

    // Auto-save selected category to Amplience whenever it changes
    useEffect(() => {
        if (!isInitialized || !sdkRef.current) {
            return
        }
        // Skip save on initial load (first run after loading from Amplience).
        // Clear the ref here so it runs after React has committed the batched state
        // updates; if we cleared it in the init effect, it would be false before this
        // effect runs (React 19 batching), causing a redundant save.
        if (isInitialLoadRef.current) {
            isInitialLoadRef.current = false
            return
        }
        void saveToAmplience(selectedCategory)
    }, [selectedCategory, isInitialized, saveToAmplience])

    /**
     * Toggle category selection - single select only.
     * If a category is already selected and user tries to select another, show error.
     * Uses functional update to avoid stale closure issues with rapid clicks.
     * Note: Saving is handled automatically by useEffect watching selectedCategory.
     */
    const handleCategoryToggle = useCallback((category: Category): void => {
        setCategorySelectionError(undefined)
        const prev = selectedCategoryRef.current

        if (prev?.id === category.id) {
            setSelectedCategory(undefined)
            return
        }
        if (prev !== undefined) {
            setCategorySelectionError('You can only select one category.')
            return
        }
        setSelectedCategory({
            id: category.id,
            name: category.name ?? category.id
        })
    }, [])

    /**
     * Remove the selected category if it matches the given id.
     * Note: Saving is handled automatically by useEffect watching selectedCategory.
     */
    const handleCategoryRemove = useCallback((categoryId: string): void => {
        setCategorySelectionError(undefined)
        setSelectedCategory((prev) => (prev?.id === categoryId ? undefined : prev))
    }, [])

    /**
     * Clear selected category.
     * Note: Saving is handled automatically by useEffect watching selectedCategory.
     */
    const handleClearAll = useCallback((): void => {
        setCategorySelectionError(undefined)
        setSelectedCategory(undefined)
    }, [])

    /**
     * Handle catalog selection change.
     * Clears selected category since it belongs to the previous catalog.
     * Does nothing if the selected catalog hasn't changed.
     * Shows confirmation dialog if there is a selected category that would be lost.
     */
    const handleCatalogChange = useCallback(
        async (catalogId: string): Promise<void> => {
            if (catalogId === selectedCatalogId) {
                return
            }

            const performCatalogChange = (): void => {
                setConfirmationModal(undefined)
                setCategorySelectionError(undefined)
                setSelectedCatalogId(catalogId)
                setSelectedCategory(undefined)
            }

            if (selectedCategory !== undefined) {
                setConfirmationModal({
                    isOpen: true,
                    title: 'Switch Catalog?',
                    message: 'Switching catalogs will clear your selected category. Continue?',
                    onConfirm: () => void performCatalogChange(),
                    onCancel: () => setConfirmationModal(undefined)
                })
                return
            }

            performCatalogChange()
        },
        [selectedCatalogId, selectedCategory]
    )

    // Don't render until SDK is initialized (credentials need to be set first)
    if (!isInitialized) {
        return (
            <div className='flex items-center justify-center bg-slate-950 py-10'>
                <div className='text-slate-400'>Initializing...</div>
            </div>
        )
    }

    return (
        <App
            selectedCategory={selectedCategory}
            onCategoryToggle={handleCategoryToggle}
            onCategoryRemove={handleCategoryRemove}
            onClearAll={handleClearAll}
            selectedCatalogId={selectedCatalogId}
            onCatalogChange={handleCatalogChange}
            errorMessage={error}
            categorySelectionError={categorySelectionError}
            confirmationModal={confirmationModal}
        />
    )
}
